// Using OpenAI Realtime API directly instead of SDK for better control
// The SDK has WebRTC issues that we can bypass with direct WebSocket connection
import { AUTH_BASE_URL } from '../config/api';

/**
 * OpenAI Agents SDK implementation for voice conversations
 * This replaces our custom implementation with the official SDK
 */
export class OpenAIAgentsSDKManager {
  constructor() {
    this.agent = null;
    this.session = null;
    this.isConnected = false;
    this.isRecording = false;
    
    // Callbacks
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onStatusCallback = null;
    this.onAudioStartCallback = null;
    this.onAudioEndCallback = null;
    
    // Settings
    this.selectedModel = 'gpt-4o-realtime-preview';
    this.selectedVoice = 'alloy';
    this.conversation = [];
    
    // Connection health monitoring
    this.connectionAttempts = 0;
    this.maxRetries = 3;
    this.retryDelay = 2000; // Start with 2 seconds
    this.heartbeatInterval = null;
    this.lastHeartbeat = null;
    
    // Realtime compatible models
    this.REALTIME_MODELS = [
      'gpt-4o-realtime-preview',
      'gpt-4o-mini-realtime-preview', 
      'gpt-4o-realtime-preview-2024-12-17',
      'gpt-4o-realtime-preview-2024-10-01'
    ];
  }

  async initialize(onMessage, onError, onStatusChange, onAudioStart, onAudioEnd) {
    this.onMessageCallback = onMessage;
    this.onErrorCallback = onError;
    this.onStatusCallback = onStatusChange;
    this.onAudioStartCallback = onAudioStart;
    this.onAudioEndCallback = onAudioEnd;

    try {
      this.onStatusCallback?.('initializing');
      
      // Connect directly to OpenAI WebSocket API
      await this.connectWithDirectWebSocket();

      this.isConnected = true;
      this.onStatusCallback?.('ready');
      return true;

    } catch (error) {
      console.error('Failed to initialize OpenAI WebSocket connection:', error);
      this.onErrorCallback?.(error);
      return false;
    }
  }

  getVoiceInstructions() {
    return `# Identity
You are Claude, a friendly and helpful AI assistant created by Anthropic. You're having a natural voice conversation with a user.

# Demeanor and Tone
- Warm, conversational, and engaging
- Patient and understanding
- Professional but approachable

# Voice Response Guidelines
- Use short, clear sentences that are easy to follow when spoken
- Avoid complex punctuation, markdown formatting, or special characters
- Don't use lists, bullet points, or numbered items
- Use natural filler words occasionally like "um" or "well" to sound more human
- Keep responses concise - aim for 1-3 sentences maximum
- Use a natural, flowing conversational pace

# Instructions
- If you need to confirm spelling of names, phone numbers, or important details, always repeat them back letter by letter
- If the user corrects any detail, acknowledge the correction naturally and confirm the new information
- Stay focused on the current conversation topic
- If you can't help with something, politely explain and offer alternatives

Remember: This is a voice conversation, so speak naturally as if talking to someone in person.`;
  }

  async connectWithDirectWebSocket() {
    try {
      // Connect to our backend WebSocket proxy instead of directly to OpenAI
      // This avoids CORS and authentication issues with direct WebSocket connections
      
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = AUTH_BASE_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${wsProtocol}//${wsHost}/realtime-proxy`;
      
      console.log('🔌 Connecting to backend WebSocket proxy:', wsUrl);
      console.log(`📊 Connection attempt ${this.connectionAttempts + 1}/${this.maxRetries}`);
      
      this.ws = new WebSocket(wsUrl);
      
      // Set up WebSocket event handlers
      this.setupWebSocketHandlers();

      // Wait for connection
      await this.waitForConnection();
      
      // Send initialization with model and settings
      console.log('🚀 Sending initialize message to proxy...');
      this.sendWebSocketMessage({
        type: 'initialize',
        model: this.selectedModel,
        voice: this.selectedVoice,
        instructions: this.getVoiceInstructions()
      });
      
      console.log('✅ Initialization message sent, waiting for OpenAI connection...');
      
      // Reset connection attempts on successful connection
      this.connectionAttempts = 0;
      this.retryDelay = 2000; // Reset retry delay
      
      // Start heartbeat monitoring
      this.startHeartbeatMonitoring();
      
      console.log('Connected to backend WebSocket proxy');
      
    } catch (error) {
      console.error('Failed to connect with WebSocket proxy:', error);
      
      // Implement retry logic
      if (this.connectionAttempts < this.maxRetries) {
        this.connectionAttempts++;
        console.log(`🔄 Retrying connection in ${this.retryDelay}ms (attempt ${this.connectionAttempts}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        this.retryDelay *= 1.5; // Exponential backoff
        
        return this.connectWithDirectWebSocket(); // Retry
      }
      
      throw error;
    }
  }

  setupWebSocketHandlers() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('Backend WebSocket proxy connection opened');
      // Don't set isConnected here - wait for connection.ready message
      this.onStatusCallback?.('initializing');
    };

    this.ws.onmessage = (event) => {
      try {
        // Handle both text and binary data from WebSocket
        if (event.data instanceof Blob) {
          console.log('Received binary data (Blob), converting to text...');
          event.data.text().then(text => {
            try {
              const message = JSON.parse(text);
              console.log('WebSocket message received (from Blob):', message.type);
              this.handleWebSocketMessage(message);
            } catch (parseError) {
              console.error('Failed to parse Blob message as JSON:', parseError);
              // Handle as raw audio data if it's not JSON
              this.handleBinaryData(event.data);
            }
          }).catch(blobError => {
            console.error('Failed to convert Blob to text:', blobError);
          });
        } else if (typeof event.data === 'string') {
          const message = JSON.parse(event.data);
          console.log('WebSocket message received:', message.type);
          this.handleWebSocketMessage(message);
        } else {
          console.log('Received unknown data type:', typeof event.data);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.onErrorCallback?.(new Error('WebSocket connection error'));
    };

    this.ws.onclose = (event) => {
      console.log('🔴 WebSocket connection closed:', event.code, event.reason);
      this.isConnected = false;
      this.stopHeartbeatMonitoring();
      this.onStatusCallback?.('disconnected');
      
      // Only attempt reconnection if it wasn't a normal closure
      if (event.code !== 1000 && event.code !== 1001 && this.connectionAttempts < this.maxRetries) {
        console.log('🔄 Unexpected closure - attempting reconnection...');
        setTimeout(() => {
          this.reconnect();
        }, this.retryDelay);
      }
    };
  }

  handleWebSocketMessage(message) {
    // Update heartbeat on any message received
    this.lastHeartbeat = Date.now();
    
    switch (message.type) {
      case 'connection.ready':
        console.log('🟢 WebSocket proxy connection ready, session:', message.sessionId);
        this.sessionId = message.sessionId;
        this.isConnected = true;
        this.onStatusCallback?.('ready');
        
        // Now set up microphone access for audio input
        this.setupMicrophoneAccess();
        break;
        
      case 'session.created':
        console.log('Session created:', message.session.id);
        
        // For hands-free mode, start listening immediately
        if (this.isConnected) {
          this.sendWebSocketMessage({
            type: 'input_audio_buffer.clear'
          });
          console.log('🎤 Audio buffer cleared, ready for input');
        }
        break;
        
      case 'response.text.delta':
      case 'response.text.done':
        if (message.delta || message.text) {
          const content = message.delta || message.text;
          this.handleTextResponse(content, message.type === 'response.text.done');
        }
        break;
        
      // Audio handling moved above with better logging
        
      case 'conversation.item.input_audio_transcription.completed':
        if (message.transcript) {
          this.handleUserTranscript(message.transcript);
        }
        break;
        
      case 'input_audio_buffer.speech_started':
        console.log('✨ User started speaking (VAD detected)');
        this.onStatusCallback?.('recording');
        this.isRecording = true;
        break;
        
      case 'input_audio_buffer.speech_stopped':
        console.log('🔇 User stopped speaking (VAD detected)');
        this.onStatusCallback?.('processing');
        this.isRecording = false;
        
        // Automatically commit the audio buffer to trigger response
        this.sendWebSocketMessage({
          type: 'input_audio_buffer.commit'
        });
        console.log('📤 Audio buffer committed for processing');
        break;
        
      case 'response.audio.delta':
        console.log('🎵 AI audio response chunk received');
        this.onAudioStartCallback?.();
        if (message.delta) {
          this.handleAudioResponse(message.delta);
        }
        break;
        
      case 'response.audio.done':
        console.log('✅ AI audio response complete');
        this.onAudioEndCallback?.();
        break;
        
      case 'conversation.item.created':
        console.log('📝 Conversation item created:', message.item?.type);
        break;
        
      case 'response.created':
        console.log('🎯 Response generation started');
        this.onStatusCallback?.('generating');
        break;
        
      case 'response.done':
        console.log('✅ Response generation complete');
        this.onStatusCallback?.('ready');
        
        // Clear the audio buffer for next input
        this.sendWebSocketMessage({
          type: 'input_audio_buffer.clear'
        });
        break;
        
      case 'response.audio_transcript.delta':
      case 'response.audio_transcript.done':
        // Handle AI speech transcription if needed
        break;
        
      case 'connection.closed':
        console.log('🔴 OpenAI connection closed by proxy');
        this.isConnected = false;
        this.stopHeartbeatMonitoring();
        this.onStatusCallback?.('disconnected');
        
        // Attempt reconnection if not intentionally closed
        if (this.connectionAttempts < this.maxRetries) {
          console.log('🔄 Attempting to reconnect after connection closed...');
          setTimeout(() => {
            this.reconnect();
          }, this.retryDelay);
        }
        break;
        
      case 'error':
        console.error('🔴 Proxy/OpenAI API error:', message.error);
        
        // Check for server errors that might need retry
        if (message.error.message && message.error.message.includes('server had an error')) {
          console.log('🔄 Server error detected, will attempt reconnection');
          // Proxy should handle reconnection automatically
        } else {
          this.onErrorCallback?.(new Error(message.error.message || 'API error'));
        }
        break;
        
      default:
        console.log('Unhandled message type:', message.type);
    }
  }

  extractMessageContent(message) {
    if (!message) return '';
    
    // Handle different message content formats
    if (typeof message.content === 'string') {
      return message.content;
    }
    
    if (Array.isArray(message.content)) {
      return message.content
        .filter(part => part.type === 'text')
        .map(part => part.text || part.content)
        .join(' ');
    }
    
    return message.content?.text || '';
  }

  setInitialConversation(conversation) {
    this.conversation = [...conversation];
    
    // Send conversation history to OpenAI if connected
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendConversationHistory(conversation);
    }
    
    console.log('Initial conversation set:', conversation.length, 'messages');
  }

  sendConversationHistory(conversation) {
    // Send existing conversation as context
    for (const message of conversation) {
      if (message.content && !message.isFromHistory) continue; // Skip non-history items
      
      this.sendWebSocketMessage({
        type: 'conversation.item.create',
        item: {
          id: `msg_${Date.now()}_${Math.random()}`,
          type: 'message',
          role: message.role === 'user' ? 'user' : 'assistant',
          content: [
            {
              type: 'input_text',
              text: message.content
            }
          ]
        }
      });
    }
  }

  setModel(model) {
    // Ensure we use a realtime-compatible model
    if (this.REALTIME_MODELS.includes(model)) {
      this.selectedModel = model;
    } else {
      // Fall back to default realtime model if provided model isn't compatible
      console.warn(`Model '${model}' not supported for realtime, using default realtime model`);
      this.selectedModel = 'gpt-4o-realtime-preview-2024-12-17';
    }
    console.log('Model updated to:', this.selectedModel);
  }

  setVoice(voice) {
    this.selectedVoice = voice;
    console.log('Voice updated to:', voice);
    
    // Note: Voice changes require session recreation with SDK
    // This is a limitation we can address in future versions
  }

  async startRecording() {
    // With the SDK, recording is handled automatically
    // The session automatically listens to microphone
    this.isRecording = true;
    this.onStatusCallback?.('recording');
    console.log('Recording started (managed by SDK)');
  }

  stopRecording() {
    // Recording stops automatically with SDK's VAD
    this.isRecording = false;
    this.onStatusCallback?.('ready');
    console.log('Recording stopped');
  }

  async startContinuousRecording() {
    // SDK handles continuous recording by default
    console.log('Continuous recording active (SDK default behavior)');
    this.onStatusCallback?.('ready');
  }

  stopContinuousRecording() {
    console.log('Continuous recording remains active (SDK manages VAD)');
  }

  triggerResponse() {
    // Manually trigger a response from the agent
    if (this.session && this.isConnected) {
      // SDK doesn't expose direct response triggering
      // It's managed by the VAD system
      console.log('Response triggering is managed by SDK VAD');
    }
  }

  sendMessage(text) {
    if (this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      // Create conversation item with text
      this.sendWebSocketMessage({
        type: 'conversation.item.create',
        item: {
          id: `msg_${Date.now()}_${Math.random()}`,
          type: 'message',
          role: 'user',
          content: [
            {
              type: 'input_text',
              text: text
            }
          ]
        }
      });
      
      // Create response
      this.sendWebSocketMessage({
        type: 'response.create',
        response: {
          modalities: ['text', 'audio']
        }
      });
      
      console.log('📝 Text message sent:', text);
      
      // Add to local conversation
      const userMessage = {
        role: 'user',
        content: text,
        timestamp: Date.now(),
        isRealtime: true,
        audio: null
      };
      
      this.conversation.push(userMessage);
      this.onMessageCallback?.(userMessage);
      
    } else {
      console.warn('⚠️ Cannot send message: WebSocket not connected');
    }
  }

  sendWebSocketMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  interrupt() {
    if (this.ws && this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.sendWebSocketMessage({
        type: 'response.cancel'
      });
      console.log('Session interrupted manually');
    }
  }

  async setupMicrophoneAccess() {
    try {
      console.log('🎤 Requesting microphone access...');
      
      // Request microphone with proper constraints for OpenAI
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      console.log('✅ Microphone access granted');
      this.audioStream = stream;
      
      // Set up audio processing to capture and send to OpenAI
      this.setupAudioCapture(stream);
      
    } catch (error) {
      console.error('❌ Failed to access microphone:', error);
      this.onErrorCallback?.(new Error(`Microphone access denied: ${error.message}`));
    }
  }
  
  setupAudioCapture(stream) {
    try {
      // Create audio context for capturing audio data
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
      
      const source = this.audioContext.createMediaStreamSource(stream);
      
      // Use AudioWorklet for better performance (fallback to ScriptProcessor)
      if (this.audioContext.audioWorklet) {
        this.setupAudioWorklet(source);
      } else {
        this.setupScriptProcessor(source);
      }
      
    } catch (error) {
      console.error('❌ Failed to setup audio capture:', error);
      this.onErrorCallback?.(error);
    }
  }
  
  setupScriptProcessor(source) {
    // Fallback to ScriptProcessor for compatibility
    const processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    
    processor.onaudioprocess = (event) => {
      if (!this.isConnected || this.ws?.readyState !== WebSocket.OPEN) return;
      
      const inputBuffer = event.inputBuffer.getChannelData(0);
      
      // Convert Float32Array to Int16Array (PCM16)
      const pcm16Buffer = new Int16Array(inputBuffer.length);
      for (let i = 0; i < inputBuffer.length; i++) {
        // Clamp to 16-bit signed integer range
        const sample = Math.max(-1, Math.min(1, inputBuffer[i]));
        pcm16Buffer[i] = sample * 0x7FFF;
      }
      
      // Convert to base64 and send to OpenAI
      const base64Audio = this.bufferToBase64(pcm16Buffer.buffer);
      
      this.sendWebSocketMessage({
        type: 'input_audio_buffer.append',
        audio: base64Audio
      });
    };
    
    source.connect(processor);
    processor.connect(this.audioContext.destination);
    this.processor = processor;
    
    console.log('🎵 Audio capture setup complete (ScriptProcessor)');
  }
  
  setupAudioWorklet(source) {
    // Modern approach using AudioWorklet (not implemented for simplicity)
    console.log('🎵 AudioWorklet not implemented, falling back to ScriptProcessor');
    this.setupScriptProcessor(source);
  }
  
  bufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  startHeartbeatMonitoring() {
    // Clear any existing heartbeat interval
    this.stopHeartbeatMonitoring();
    
    this.lastHeartbeat = Date.now();
    this.heartbeatInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastHeartbeat = now - this.lastHeartbeat;
      
      // If no heartbeat for 30 seconds, consider connection stale
      if (timeSinceLastHeartbeat > 30000) {
        console.warn('⚠️ No heartbeat for 30 seconds, connection may be stale');
        if (this.isConnected && !this.isReconnecting && this.connectionAttempts < this.maxRetries) {
          console.log('🔄 Triggering reconnection due to stale connection');
          this.scheduleReconnection();
        }
      }
    }, 10000); // Check every 10 seconds
  }
  
  stopHeartbeatMonitoring() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
  
  scheduleReconnection() {
    if (this.isReconnecting) {
      console.log('🔄 Reconnection already scheduled, skipping');
      return;
    }
    
    this.isReconnecting = true;
    
    setTimeout(() => {
      this.reconnect();
    }, this.retryDelay);
  }
  
  async reconnect() {
    if (!this.isReconnecting) {
      console.log('🔄 Reconnection not scheduled, skipping');
      return;
    }
    
    console.log('🔄 Starting reconnection process...');
    
    // Clean up current connection
    if (this.ws) {
      this.ws.close(1000, 'Reconnecting');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.stopHeartbeatMonitoring();
    
    try {
      // Attempt to reconnect
      await this.connectWithDirectWebSocket();
      console.log('✅ Reconnection successful');
      this.isReconnecting = false;
    } catch (error) {
      console.error('❌ Reconnection failed:', error);
      this.isReconnecting = false;
      this.connectionState = 'error';
      this.onErrorCallback?.(new Error('Reconnection failed: ' + error.message));
    }
  }
  
  cleanup() {
    // Stop heartbeat monitoring
    this.stopHeartbeatMonitoring();
    
    // Clean up audio input resources
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }
    
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    // Clean up audio playback resources
    if (this.playbackContext && this.playbackContext.state !== 'closed') {
      this.playbackContext.close();
      this.playbackContext = null;
    }
    
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'Client cleanup');
    }
    
    // Reset connection state
    this.isConnected = false;
    this.isRecording = false;
    this.conversation = [];
    this.ws = null;
    this.connectionAttempts = 0;
    this.retryDelay = 2000;
    this.isReconnecting = false;
    this.connectionState = 'idle';
    
    console.log('🧹 OpenAI WebSocket connection and audio cleaned up');
  }

  getConversation() {
    return this.conversation;
  }

  clearConversation() {
    this.conversation = [];
    
    // Clear conversation by creating new session if needed
    if (this.ws && this.isConnected) {
      this.sendWebSocketMessage({
        type: 'conversation.item.truncate',
        content_index: 0
      });
    }
    
    console.log('Conversation cleared');
  }

  async waitForConnection() {
    return new Promise((resolve, reject) => {
      if (this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }
      
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000);
      
      this.ws.onopen = () => {
        clearTimeout(timeout);
        resolve();
      };
      
      this.ws.onerror = (error) => {
        clearTimeout(timeout);
        reject(error);
      };
    });
  }

  async configureSession() {
    // Configuration is now handled in onopen
    console.log('Session configuration sent in onopen handler');
  }

  handleTextResponse(content, isDone) {
    const message = {
      role: 'assistant',
      content: content,
      timestamp: Date.now(),
      isRealtime: true,
      audio: null
    };

    this.conversation.push(message);
    this.onMessageCallback?.(message);
    
    if (isDone) {
      console.log('Text response completed:', content);
    }
  }

  handleAudioResponse(audioData) {
    // Handle incoming audio data
    this.onAudioStartCallback?.();
    
    // Process audio playback here if needed
    console.log('Audio response received, length:', audioData.length);
  }

  handleUserTranscript(transcript) {
    console.log('User speech transcribed:', transcript);
    
    const message = {
      role: 'user',
      content: transcript,
      timestamp: Date.now(),
      isRealtime: true,
      audio: null
    };

    this.conversation.push(message);
    this.onMessageCallback?.(message);
  }

  handleBinaryData(blobData) {
    // Handle binary audio data from OpenAI
    console.log('Received binary audio data:', blobData.size, 'bytes');
    
    // Convert blob to audio and play it
    this.playAudioData(blobData);
  }
  
  async playAudioData(blobData) {
    try {
      this.onAudioStartCallback?.();
      
      // Create audio context for playback if not exists
      if (!this.playbackContext) {
        this.playbackContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Convert blob to array buffer
      const arrayBuffer = await blobData.arrayBuffer();
      
      // Decode audio data
      const audioBuffer = await this.playbackContext.decodeAudioData(arrayBuffer);
      
      // Create and play audio source
      const source = this.playbackContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.playbackContext.destination);
      
      source.onended = () => {
        this.onAudioEndCallback?.();
        console.log('🔊 Audio playback finished');
      };
      
      source.start();
      console.log('🔊 Playing AI audio response');
      
    } catch (error) {
      console.error('❌ Failed to play audio:', error);
      this.onAudioEndCallback?.();
      
      // Fallback: create a simple audio element
      try {
        const audioUrl = URL.createObjectURL(blobData);
        const audio = new Audio(audioUrl);
        audio.onended = () => {
          this.onAudioEndCallback?.();
          URL.revokeObjectURL(audioUrl);
        };
        audio.play();
        console.log('🔊 Playing audio via Audio element (fallback)');
      } catch (fallbackError) {
        console.error('❌ Audio playback fallback failed:', fallbackError);
        this.onAudioEndCallback?.();
      }
    }
  }
}

// Export singleton instance
export const openaiAgentsSDKManager = new OpenAIAgentsSDKManager();